import { useQueryClient } from '@tanstack/react-query'
import StyledLoader from 'components/Elements/StyledLoader'
import StyledTooltip from 'components/Elements/StyledTooltip'
import SectionTitle from 'components/SectionTitle'
import AdditionalBooksModal from 'sections/Dashboard/components/AdditionalBooksModal'
import ShippingAddressModal from 'sections/Dashboard/components/ShippingAddressModal'
import { useRef, useState } from 'react'
import { Button } from 'react-bootstrap'
import { BiMoveVertical } from 'react-icons/bi'
import { FaCheck, FaEye, FaPencilRuler, FaTruck } from 'react-icons/fa'
import { LuFileCog } from 'react-icons/lu'
import { Link, useParams } from 'react-router-dom'
import axiosClient from 'services/axios-client'
import { useBookQuery } from 'store/queries'
import PublishTasksContainer from './style'

function PublishTasks() {
  const { bookId } = useParams()
  const queryClient = useQueryClient()

  const { data: book } = useBookQuery(bookId)
  const onUpdateBook = () =>
    queryClient.invalidateQueries({ queryKey: ['book', bookId] })
  const [showShippingAddressModal, setShowShippingAddressModal] =
    useState(false)
  const [showAdditionalBooksModal, setShowAdditionalBooksModal] =
    useState(false)
  const [pubError, setPubError] = useState(false)
  const panelRef = useRef(null)

  const isPublishable =
    !book.is_published &&
    book.story_order_confirmed &&
    book.preview_confirmed &&
    book.cover_confirmed &&
    book.quantity_confirmed &&
    book.address_confirmed

  if (!book.id) return <StyledLoader />

  const onPublish = () => {
    setPubError('')
    axiosClient
      .post(`/book/${bookId}/publish`)
      .then(() => queryClient.invalidateQueries({ queryKey: ['book', bookId] }))
      .catch((e) => {
        setPubError(e)
      })
  }

  const BeginPublishDialog = () => (
    <div>
      <div className="p20">
        <SectionTitle>Publishing Tasks</SectionTitle>

        <p>
          This should be done when you are finished creating the book. No new
          story prompts will be sent, although you can still edit and update
          existing stories. We will guide you through the steps to review and
          publish the book.
        </p>
        <p>Here is a preview of the tasks:</p>

        <StyledTooltip
          title="Confirm the chapter order of your book."
          placement="topLeft"
        >
          <div className="bb-line publish-task task-preview">
            <BiMoveVertical size={20} className="icon-option" />
            <p className="pl20 task-name">Approve Story Order</p>
          </div>
        </StyledTooltip>
        <StyledTooltip
          title="View a preview of your book and make sure everything looks great."
          placement="topLeft"
        >
          <div className="bb-line publish-task task-preview">
            <FaEye size={20} className="icon-option" />
            <p className="pl20 task-name">Approve Book Preview</p>
          </div>
        </StyledTooltip>
        <StyledTooltip
          title="Design a cover for your book."
          placement="topLeft"
        >
          <div className="bb-line publish-task task-preview cover-task">
            <FaPencilRuler size={20} className="icon-option" />
            <p className="pl20 task-name">Approve Cover Design</p>
          </div>
        </StyledTooltip>
        <StyledTooltip
          title="Make sure your shipping details are accurate."
          placement="topLeft"
        >
          <div className="bb-line publish-task task-preview">
            <FaTruck size={20} className="icon-option" />
            <p className="pl20 task-name">Verify Shipping Address</p>
          </div>
        </StyledTooltip>
        <StyledTooltip
          title="Confirm you will receive enough copies of the book and order more if neccessary."
          placement="topLeft"
        >
          <div className="bb-line publish-task task-preview">
            <LuFileCog size={20} className="icon-option" />
            <p className="pl20 task-name">Verify Book Quantity</p>
          </div>
        </StyledTooltip>
      </div>

      <div className="edit-container modal-custom-footer">
        <div className="publish-btns">
          <Button
            className="btn-blue btn-dash ml-2"
            onClick={() => {
              axiosClient
                .put(`/book/${book?.id}`, {
                  is_publish_started: true,
                })
                .then((res) => {
                  if (res.ok) {
                    queryClient.invalidateQueries({
                      queryKey: ['book', bookId],
                    })
                  }
                })
            }}
          >
            Start Publishing Tasks
          </Button>
        </div>
      </div>
    </div>
  )

  const PublishTasks = () => (
    <div className="activity-group">
      <div className="bb-line">
        <Link to={`/dashboard/book/${bookId}/stories/order`}>
          <div className="bb-line publish-task">
            <BiMoveVertical size={20} className="icon-option" />
            <p className="pl20">Approve Story Order</p>
            {book?.story_order_confirmed && (
              <FaCheck className="completed-check" />
            )}
          </div>
        </Link>

        <Link to={`/dashboard/book/${bookId}/publish/preview`}>
          <div className="bb-line publish-task">
            <FaEye size={20} className="icon-option" />
            <p className="pl20">Approve Book Preview</p>
            {book?.preview_confirmed && <FaCheck className="completed-check" />}
          </div>
        </Link>

        <Link to={`/dashboard/book/${bookId}/publish/cover-templates`}>
          <div className="bb-line publish-task cover-task">
            <FaPencilRuler size={20} className="icon-option" />
            <p className="pl20">Book Cover Design Approved</p>
            {book?.cover_confirmed && <FaCheck className="completed-check" />}
          </div>
        </Link>

        <div
          onClick={() => setShowShippingAddressModal(true)}
          className="bb-line publish-task"
        >
          <FaTruck size={20} className="icon-option" />
          <p className="pl20">Verify Shipping Address</p>
          {book?.address_confirmed && <FaCheck className="completed-check" />}
        </div>

        <div
          onClick={() => setShowAdditionalBooksModal(true)}
          className="bb-line publish-task"
        >
          <LuFileCog size={20} className="icon-option" />
          <p className="pl20">Verify Book Quantity</p>
          {book?.quantity_confirmed && <FaCheck className="completed-check" />}
        </div>
      </div>

      <div className="publish-btns text-center">
        {pubError ? <p className="error help is-danger">{pubError}</p> : <></>}
        {book.is_published ? (
          <Button disabled className="btn-secondary cancel tasks-details-btn">
            Project has been completed
          </Button>
        ) : (
          <Button
            disabled={!isPublishable}
            className="btn-blue btn-dash publish-btn tasks-details-btn"
            onClick={onPublish}
          >
            Finalize Project & Publish Book
          </Button>
        )}
      </div>
    </div>
  )

  return (
    <PublishTasksContainer ref={panelRef} className="section-container">
      {book.is_publish_started ? <PublishTasks /> : <BeginPublishDialog />}

      {showShippingAddressModal && (
        <ShippingAddressModal
          bookID={bookId}
          purchaserID={book.purchaser_user_id}
          show={showShippingAddressModal}
          onHide={() => setShowShippingAddressModal(false)}
          panelRef={panelRef}
          onUpdate={onUpdateBook}
          disabled={book.is_published}
        />
      )}
      {showAdditionalBooksModal && (
        <AdditionalBooksModal
          bookID={bookId}
          show={showAdditionalBooksModal}
          onHide={() => setShowAdditionalBooksModal(false)}
          panelRef={panelRef}
          onUpdate={onUpdateBook}
          disabled={book.is_published}
        />
      )}
    </PublishTasksContainer>
  )
}

export default PublishTasks
